import React from 'react';

import { Button } from '@tanium/react-button';
import { FormElementContainer, Modal, ModalProps } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';

import { useTranslation } from './localization';

export interface Props extends Pick<ModalProps, 'isOpen' | 'onRequestClose' | 'size'> {
  /**
   * Label to display on Cancel button, defaults to 'Cancel'.
   */
  cancelLabel?: string;
  /**
   * Content to appear in modal between headers and footer buttons.
   */
  children?: React.ReactNode;
  /**
   * Label to display on Confirm button, defaults to 'Confirm'.
   */
  confirmLabel?: string;
  /**
   * Header content in modal.
   */
  header?: React.ReactNode;
  /**
   * Whether the Confirm button should be disabled or not.
   * Defaults to false.
   */
  isConfirmDisabled?: boolean;
  /**
   * Callback for click of Cancel button.
   */
  onCancel: () => void;
  /**
   * Callback for click of Confirm button.
   */
  onConfirm: () => void;
  /**
   * Subheader content in modal.
   */
  subheader?: React.ReactNode;
}

/**
 * A modal dialog with a confirm and a cancel button.
 */
const ConfirmModal = ({
  cancelLabel,
  children,
  confirmLabel,
  header,
  isConfirmDisabled = false,
  isOpen,
  onCancel,
  onConfirm,
  onRequestClose,
  subheader,
  size,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size={size}
      container={FormElementContainer}
      header={header}
      subheader={subheader}
      footer={
        <Stack inline itemSpacing={1}>
          <Button
            onClick={() => {
              onConfirm();
            }}
            disabled={isConfirmDisabled}
          >
            {confirmLabel ?? t('Confirm')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              onCancel();
            }}
          >
            {cancelLabel ?? t('Cancel')}
          </Button>
        </Stack>
      }
    >
      {children}
    </Modal>
  );
};

export default ConfirmModal;
